/**
 * <configurator-quiz> — steps through the questions rendered by
 * sections/collection-configurator-quiz.liquid. On finish it asks the
 * Storefront API for the collection's products (minimal data: handle, tag ids
 * and the product metafields the quiz cares about), matches them against the
 * selected configurator_tag ids (AND within a group, AND/OR across groups per
 * the merchant's match-mode setting), then renders the matches as a
 * product-grid-style screen.
 *
 * Each card is the theme's real collection-product-card, fetched by handle
 * through the Section Rendering API (sections/configurator-product-card.liquid),
 * so the result screen looks exactly like the collection product grid.
 *
 * Selections are keyed by group; each option carries the id of its
 * configurator_tag, which is what the matching step compares. A group's
 * `default_selected` tags arrive marked data-locked: they start in the
 * selection and no interaction takes them out again (see readLocks); a
 * required group without them opens on its first compatible option. A group's
 * `conditional_tags` rules mark choices that conflict with answers in other
 * groups. Those choices stay visible and clickable; the newest click wins and
 * older incompatible answers are adjusted by resolveConditionalSelections.
 * A product satisfies a tag either because its custom.configurator_tags
 * metafield lists that tag, or because the tag's `connected` mapping points at
 * a product metafield the product already carries (see parseConnected) — the
 * second route is what lets a shop reuse the metafields it already maintains
 * instead of re-tagging every product.
 *
 * State survives a reload from two places: the answers and the current step are
 * mirrored into the query string, and the fetched catalogue is kept in
 * localStorage under a short TTL — so a refresh restores the same screen
 * without a single Storefront API call.
 *
 * The result screen is where all of this network cost used to land, so three
 * things move it off there:
 *
 *   - the catalogue read starts at the first answer, not at finish, so it runs
 *     while the shopper is still working through the questions (warmProducts);
 *   - the cards of a page are requested in parallel and each one is placed the
 *     moment it arrives, instead of the grid waiting on the slowest of them
 *     (renderNextPage);
 *   - a rendered card is kept for the rest of the page view, so adjusting a
 *     facet re-renders the products it already has instead of asking the
 *     Section Rendering API for the same markup again (cardTemplate).
 */
/**
 * Reduce a configurator_tag id to its trailing numeric segment so the two
 * shapes we compare match: DOM data-value holds the numeric id from Liquid's
 * `metaobject.system.id`, while the Storefront API returns a full GID
 * (gid://shopify/Metaobject/NNNN).
 */
const normalizeTagId = (id) => String(id).split("/").pop();

/** Values are compared case- and whitespace-insensitively throughout. */
const normalizeValue = (value) => String(value).trim().toLowerCase();

/**
 * A condition's `match` and a group's `type` are machine values ("all",
 * "single_choice") kept in plain text fields, so an auto-translate run rewrites
 * them along with the copy — "all" comes back as "tout", "tutti" or "все" on
 * the translated storefronts, and every AND rule would quietly turn into OR.
 * These fold the spellings the store's languages produce back onto the values
 * the quiz compares against. Anything unrecognised takes the wider reading
 * ("any", "single_choice") and is reported in the theme editor, so a new
 * language shows up as a warning rather than as a question that appears or
 * disappears for no visible reason.
 */
const MATCH_ALL = /^(all|alle|tou(?:s|t|tes)|tutt[oi]|tod[oa]s?|все)$/i;
const MATCH_ANY =
  /^(any|beliebig|n'importe quel(?:le)?|qualsiasi|cualquier[ao]?|любой)$/i;
const TYPE_KNOWN = /^(single_choice|multi_choice)$/;
const TYPE_MULTI = /mult|múlt|mehrfach|множ/i;

const normalizeMatch = (raw) => {
  const text = normalizeValue(raw || "any");
  if (text === "all" || text === "any") return { value: text, known: true };
  if (MATCH_ALL.test(text)) return { value: "all", known: true };
  if (MATCH_ANY.test(text)) return { value: "any", known: true };
  return { value: "any", known: false };
};

const normalizeGroupType = (raw) => {
  const text = normalizeValue(raw || "single_choice");
  if (TYPE_KNOWN.test(text)) return { value: text, known: true };
  return {
    value: TYPE_MULTI.test(text) ? "multi_choice" : "single_choice",
    known: false,
  };
};

/**
 * Parse a configurator_tag's `connected` field — the product metafield that
 * already carries this option's value, so the quiz can match on the data a shop
 * maintains anyway instead of on a second round of tagging.
 *
 *   custom.material=aluminium     metafield custom.material must be "aluminium"
 *   custom.material               metafield custom.material only has to be set
 *   spec.a=1; spec.b=2            several mappings, OR-ed
 *
 * Returns `{ namespace, key, value }[]`; unparseable entries are dropped rather
 * than thrown, so one mistyped tag cannot take the whole quiz down.
 */
const parseConnected = (raw) =>
  String(raw || "")
    .split(/[;\n]+/)
    .map((entry) => {
      const text = entry.trim();
      if (!text) return null;

      const equals = text.indexOf("=");
      const identifier = (equals === -1 ? text : text.slice(0, equals)).trim();
      const value = equals === -1 ? "" : text.slice(equals + 1).trim();

      // Namespaces and keys cannot contain a dot themselves, so the first one
      // is always the separator.
      const dot = identifier.indexOf(".");
      if (dot < 1 || dot === identifier.length - 1) return null;

      return {
        namespace: identifier.slice(0, dot),
        key: identifier.slice(dot + 1),
        value: normalizeValue(value),
      };
    })
    .filter(Boolean);

/**
 * Every spelling of one product metafield value a merchant could reasonably
 * have written into a tag's `connected` field. A list metafield arrives as a
 * JSON array, a reference metafield as a GID — both should match the plain
 * value someone types in the metaobject editor.
 */
const expandMetafieldValues = (raw) => {
  const values = new Set();

  const add = (value) => {
    const text = normalizeValue(value);
    if (!text) return;
    values.add(text);
    if (text.startsWith("gid://")) values.add(text.split("/").pop());
  };

  add(raw);
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      parsed.forEach((item) => {
        if (item !== null && typeof item !== "object") add(item);
      });
    } else if (parsed !== null && typeof parsed !== "object") {
      add(parsed);
    }
  } catch (error) {
    // Plain text metafield — the raw value is all there is.
  }

  return values;
};

/**
 * `namespace.key` -> every spelling of that product metafield's value.
 * `metafields(identifiers:)` answers positionally and puts null where the
 * product has no value, so the nulls are skipped rather than indexed over.
 */
const toFieldMap = (metafields) => {
  const fields = new Map();
  (metafields || []).forEach((field) => {
    if (!field || field.value == null) return;
    fields.set(
      `${field.namespace}.${field.key}`,
      expandMetafieldValues(field.value),
    );
  });
  return fields;
};

/** Storefront API cap on `metafields(identifiers:)` per query. */
const MAX_METAFIELD_IDENTIFIERS = 250;

/** Products per `nodes(ids:)` lookup, matching the catalogue's page size. */
const PRIMARY_READ_BATCH = 250;

/** djb2, only ever used to keep the cache key short. */
const hashString = (text) => {
  let hash = 5381;
  for (let i = 0; i < text.length; i++) {
    hash = ((hash << 5) + hash + text.charCodeAt(i)) | 0;
  }
  return (hash >>> 0).toString(36);
};

/**
 * Turn a storefront ISO code ("de", "pt-BR") into the Storefront API enum
 * spelling ("DE", "PT_BR"). Returns null when the code is missing, which the
 * @inContext directive accepts as "use the default".
 */
const localeEnum = (code) =>
  code ? code.trim().toUpperCase().replace(/-/g, "_") : null;

/**
 * Query-string keys the quiz owns. Everything else in the URL is left alone, so
 * a shared link keeps its sort_by / utm_* / preview params.
 *
 *   ?ccq=123,456      the selected configurator_tag ids, in group order
 *   ?ccq_step=3       the step being shown, or `results` once the quiz is done
 *
 * Tag ids rather than group ids: ids are stable, whereas a group id is
 * positional (`q1-g1`) and shifts the moment a merchant reorders questions.
 * A shared URL therefore survives edits to the quiz.
 */
const PARAM_TAGS = "ccq";
const PARAM_VIEW = "ccq_step";
const VIEW_RESULTS = "results";

/** Shared empty set, so lockedIn() can answer without allocating per call. */
const EMPTY_SET = new Set();

/**
 * Product cache. The catalogue read is the expensive part of the result screen
 * — up to 40 Storefront API round-trips — and it is identical for every shopper
 * on the same collection, so it is worth keeping across a reload. Bump the
 * version segment whenever the stored shape changes.
 */
const CACHE_ROOT = "ccq:";
const CACHE_PREFIX = `${CACHE_ROOT}v3:`;
const DEFAULT_CACHE_TTL_MINUTES = 15;

class ConfiguratorQuiz extends HTMLElement {
  connectedCallback() {
    this.steps = Array.from(this.querySelectorAll(".ccq__step"));
    if (this.steps.length === 0) return;

    this.intro = this.querySelector(".ccq__intro");
    this.card = this.querySelector(".ccq__card");
    this.result = this.querySelector(".ccq__result");
    this.resultsEl = this.querySelector(".ccq__results");
    this.countEl = this.querySelector(".ccq__results-count");
    this.statusEl = this.querySelector(".ccq__results-status");
    this.gridEl = this.querySelector(".ccq__results-grid");
    this.loaderEl = this.querySelector(".ccq__loader");
    this.moreBtn = this.querySelector(".ccq__results-more");
    this.progressEl = this.querySelector(".ccq__progress");
    this.barEl = this.querySelector(".ccq__bar");
    this.stepNum = this.querySelector(".ccq__step-num");
    this.stepTotal = this.querySelector(".ccq__step-total");
    this.stepName = this.querySelector(".ccq__step-name");
    this.backBtn = this.querySelector(".ccq__btn--back");
    this.skipBtn = this.querySelector(".ccq__btn--skip");
    this.nextBtn = this.querySelector(".ccq__btn--next");
    this.restartBtn = this.querySelector(".ccq__btn--restart");
    this.facetsEl = this.querySelector(".ccq__facets");
    this.facetsResetBtn = this.querySelector(".ccq__facets-reset");
    this.adjustmentStatusEls = Array.from(
      this.querySelectorAll("[data-ccq-adjustment-status]"),
    );
    this.adjustsSelectionLabel = this.dataset.adjustsSelection || "";
    this.adjustmentSingle = this.dataset.adjustmentSingle || "";
    this.adjustmentMultiple = this.dataset.adjustmentMultiple || "";

    this.nextLabel = this.nextBtn.dataset.labelNext;
    this.finishLabel = this.nextBtn.dataset.labelFinish;

    // `|| 15` would swallow a deliberate 0 (= caching off), so parse first.
    const cacheTtl = parseInt(this.dataset.cacheTtl, 10);

    // Storefront API configuration for the product-matching step.
    this.config = {
      shopDomain: this.dataset.shopDomain,
      collectionHandle: this.dataset.collectionHandle,
      token: this.dataset.storefrontToken,
      apiVersion: this.dataset.apiVersion || "2026-07",
      matchMode: this.dataset.matchMode === "any" ? "any" : "all",
      pageSize: Math.max(1, parseInt(this.dataset.pageSize, 10) || 12),
      language: localeEnum(this.dataset.language),
      country: localeEnum(this.dataset.country),
      primaryLanguage: localeEnum(this.dataset.primaryLanguage),
      cacheTtl: Number.isFinite(cacheTtl)
        ? Math.max(0, cacheTtl)
        : DEFAULT_CACHE_TTL_MINUTES,
    };

    // In the theme editor a merchant is usually mid-edit on the very tags and
    // products this caches, so serving them a 15-minute-old catalogue would
    // read as "my change did nothing". Always go to the API there.
    this.isDesignMode = Boolean(window.Shopify && window.Shopify.designMode);

    // Section Rendering API section that renders one product card.
    this.cardSection = this.resultsEl.dataset.cardSection;
    this.moreBtn.textContent = this.resultsEl.dataset.moreText;

    // handle -> Promise<HTMLTemplateElement|null>, the rendered card markup.
    // Kept for the page view so re-filtering does not re-fetch cards it has
    // already rendered once — see cardTemplate.
    this.cardCache = new Map();

    // A card carries the cart into its markup — the buy button renders how many
    // of that variant are already in there — so a card rendered before an
    // add-to-cart is out of date after it. The theme publishes every cart
    // change on this channel (assets/pubsub.js, loaded in theme.liquid); drop
    // the whole cache on one rather than work out which card moved, since the
    // only cost is that the next filter change re-fetches, exactly as it did
    // before the cache existed. Guarded: without pubsub.js the quiz still runs,
    // it just never caches a stale cart quantity because it never sees a cart.
    if (typeof subscribe === "function") {
      this.cartUpdateUnsubscriber = subscribe("cart-update", () =>
        this.cardCache.clear(),
      );
    }

    // groupId -> Set of selected option values
    this.selections = new Map();
    this.selectionPriorities = new Map();
    this.interactionPriority = 0;
    this.querySelectorAll(".ccq__group").forEach((group) => {
      this.selections.set(group.dataset.groupId, new Set());
      this.selectionPriorities.set(group.dataset.groupId, 0);
    });

    this.normalizeMachineValues();
    this.readLocks();
    this.readConnections();
    this.readBranching();
    this.readConditionals();
    this.buildProgressSegments();

    this.current = 0;

    this.addEventListener("click", this.onClick);
    this.backBtn.addEventListener("click", () => this.goBack());
    this.nextBtn.addEventListener("click", () => this.onNext());
    this.skipBtn.addEventListener("click", () => this.onSkip());
    this.restartBtn.addEventListener("click", () => this.restart());
    this.moreBtn.addEventListener("click", () => this.renderNextPage());

    if (this.facetsEl) {
      this.facetsEl.addEventListener("change", this.onFacetChange);
      // `toggle` does not bubble, so it is caught on the way down.
      this.facetsEl.addEventListener("toggle", this.onFacetToggle, true);
      this.facetsResetBtn.addEventListener("click", () => this.resetFacets());

      this.facetListScrollTops = new WeakMap();
      this.facetItemObserver = new ResizeObserver(this.onFacetItemResize);
      this.facetsEl
        .querySelectorAll(".ccq__facet-item")
        .forEach((item) => this.facetItemObserver.observe(item));
    }

    // GTM tracking, when collection-configurator-quiz-tracking.js is on the
    // page. Every call site below goes through `?.`, so dropping that script
    // turns the dataLayer pushes off and changes nothing else.
    this.tracking = window.ConfiguratorQuizTracking
      ? new window.ConfiguratorQuizTracking(this)
      : null;

    // Selections and the step come out of the URL before the first paint, so a
    // reload lands exactly where the shopper left off instead of on step 1.
    const showResults = this.restoreFromUrl();
    // A result screen restored from a shared link never walked the questions:
    // the step render() paints underneath it was asked of nobody.
    if (showResults) this.tracking?.suspend();
    this.render();
    // Restoring is not navigation — the page is already at the top and the
    // browser may be mid scroll-restore, so skip finish()'s scroll here.
    if (showResults) {
      this.finish({ scroll: false, restored: true });
      this.tracking?.resume();
    }
    this.tracking?.observe();
  }

  disconnectedCallback() {
    this.facetItemObserver?.disconnect();
    // The theme editor tears sections down and rebuilds them; without this the
    // dead element stays subscribed and clears a cache nobody reads again.
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
      this.cartUpdateUnsubscriber = null;
    }
  }

  /**
   * Seed the answers a group pins. A group's `default_selected` tags are marked
   * data-locked by Liquid; they start selected and no interaction may take them
   * out again, so they are added here and put straight back by every path that
   * clears a group (skip, reset, restart, swapping a single-choice answer).
   *
   * They satisfy a required group on their own, which is exactly the point:
   * the shopper is never asked a question whose answer the merchant already
   * fixed. They narrow products until the shopper selects a value of their own.
   */
  readLocks() {
    // groupId -> Set of option values that cannot be deselected
    this.lockedByGroup = new Map();
    this.querySelectorAll(".ccq__group").forEach((group) => {
      const locked = new Set();
      const selected = this.selections.get(group.dataset.groupId);
      group
        .querySelectorAll('.ccq__opt[data-locked="true"]')
        .forEach((option) => {
          locked.add(option.dataset.value);
          selected.add(option.dataset.value);
        });
      this.lockedByGroup.set(group.dataset.groupId, locked);
    });
  }

  /** The values one group pins; empty for a group without defaults. */
  lockedIn(groupId) {
    return this.lockedByGroup.get(groupId) || EMPTY_SET;
  }

  /** Empty a group down to its pinned answers. */
  clearGroup(groupId) {
    const selected = this.selections.get(groupId);
    if (!selected) return;
    selected.clear();
    this.lockedIn(groupId).forEach((value) => selected.add(value));
  }

  /** Has the shopper answered this group themselves, pins aside? */
  hasOwnSelection(groupId) {
    const selected = this.selections.get(groupId);
    if (!selected || selected.size === 0) return false;
    const locked = this.lockedIn(groupId);
    for (const value of selected) {
      if (!locked.has(value)) return true;
    }
    return false;
  }

  /**
   * Collect the `connected` mappings the options carry, and from them the
   * distinct product metafields the catalogue read has to ask for. Both are
   * fixed for the life of the element — the options come from Liquid — so this
   * runs once rather than per match.
   */
  readConnections() {
    // tagId -> matchers; a tag with no mapping simply never appears here and
    // keeps matching purely through custom.configurator_tags.
    this.tagConnections = new Map();
    const identifiers = new Map();

    this.querySelectorAll(".ccq__opt").forEach((option) => {
      const matchers = parseConnected(option.dataset.connected);
      if (matchers.length === 0) return;

      this.tagConnections.set(normalizeTagId(option.dataset.value), matchers);
      matchers.forEach(({ namespace, key }) => {
        identifiers.set(`${namespace}.${key}`, { namespace, key });
      });
    });

    this.metafieldIdentifiers = Array.from(identifiers.values());
    if (this.metafieldIdentifiers.length > MAX_METAFIELD_IDENTIFIERS) {
      // Far beyond any realistic quiz, but a silent truncation here would read
      // as "some options match nothing" and be near-impossible to diagnose.
      console.warn(
        "[configurator-quiz] more than",
        MAX_METAFIELD_IDENTIFIERS,
        "connected product metafields; the surplus is ignored",
      );
      this.metafieldIdentifiers = this.metafieldIdentifiers.slice(
        0,
        MAX_METAFIELD_IDENTIFIERS,
      );
    }
  }

  /**
   * Read the groups' "Conditional tags" rules — the `conditional_tags` JSON
   * metafield, as `{ "<tag handle>": ["<tag handle>", …] }`.
   *
   * A rule reads "while <key> is selected in another group, this group offers
   * only these options". Merchants write handles, the quiz compares option
   * values (configurator_tag ids), so the handles are resolved to values once
   * here rather than on every answer.
   */
  readConditionals() {
    // Every spelling the JSON may use for one option: the configurator_tag's
    // metaobject handle, plus the plain `tag` value it carries — that is the
    // string a merchant sees on the products, so it is worth accepting too.
    // Compared case-insensitively, like every other value in this file.
    this.handlesOfValue = new Map(); // option value -> Set of handles
    this.valueOfHandle = new Map(); // handle -> option value

    const options = Array.from(this.querySelectorAll(".ccq__opt"));
    const register = (option, raw) => {
      const handle = normalizeValue(raw || "");
      if (!handle) return;
      const value = option.dataset.value;
      const handles = this.handlesOfValue.get(value) || new Set();
      handles.add(handle);
      this.handlesOfValue.set(value, handles);
      if (!this.valueOfHandle.has(handle))
        this.valueOfHandle.set(handle, value);
    };

    // Metaobject handles first, and only then the `tag` aliases: two tags can
    // carry the same `tag` value, and one of those must never end up claiming
    // a name that is another tag's actual handle.
    options.forEach((option) => register(option, option.dataset.handle));
    options.forEach((option) => register(option, option.dataset.tagName));

    // groupId -> [{ key, values }]; a group without the metafield never
    // appears here and keeps offering all of its options.
    this.conditionalByGroup = new Map();
    this.querySelectorAll(".ccq__group").forEach((group) => {
      const rules = this.readConditionalRules(group);
      if (rules.length > 0) {
        this.conditionalByGroup.set(group.dataset.groupId, rules);
      }
    });

    // Options that conflict with the current answers. They stay visible and
    // clickable; selecting one gives it priority and adjusts older answers.
    this.conflictingOptions = new Map(); // groupId -> Set of option values
    this.seeded = new Set(); // required groups already pre-answered once
  }

  /**
   * One group's `conditional_tags` entries, with the handles resolved to option
   * values. A malformed attribute is reported and treated as "no rules": a
   * merchant typo should cost the group its rules, not take the quiz down.
   */
  readConditionalRules(group) {
    const raw = group.dataset.conditionalTags;
    if (!raw) return [];

    let parsed;
    try {
      parsed = JSON.parse(raw);
      // A json metafield can come through already encoded as a string, where
      // one parse only strips the quotes off the object.
      if (typeof parsed === "string") parsed = JSON.parse(parsed);
    } catch (error) {
      console.error(
        "[configurator-quiz] unreadable conditional_tags",
        raw,
        error,
      );
      return [];
    }
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed))
      return [];

    const where = group.dataset.heading || group.dataset.groupId;
    return Object.entries(parsed)
      .map(([key, allowed]) => {
        const values = new Set();
        (Array.isArray(allowed) ? allowed : [allowed]).forEach((handle) => {
          const value = this.valueOfHandle.get(normalizeValue(handle));
          if (value) values.add(value);
          else if (this.isDesignMode) {
            console.warn(
              "[configurator-quiz] conditional_tags in group",
              where,
              "names a tag handle no option carries:",
              handle,
            );
          }
        });
        return { key: normalizeValue(key), values };
      })
      .filter((rule) => rule.key);
  }

  /**
   * Fold translated machine values back onto the ones the quiz compares. A
   * group's `type` is read from data-type in several places, so it is
   * rewritten in place once here; a condition's `match` is folded in
   * readRules, where the rules are built.
   */
  normalizeMachineValues() {
    this.querySelectorAll(".ccq__group").forEach((group) => {
      const type = normalizeGroupType(group.dataset.type);
      if (!type.known && this.isDesignMode) {
        console.warn(
          "[configurator-quiz] group",
          group.dataset.heading || group.dataset.groupId,
          "has an unrecognised (translated?) type",
          JSON.stringify(group.dataset.type),
          "— reading it as",
          type.value + ". Clear the translation of the group's `type` field.",
        );
      }
      group.dataset.type = type.value;
    });
  }

  /**
   * Read the branching rules Liquid put on the steps, and note which step each
   * group belongs to. Both are fixed for the life of the element; only which
   * rules *hold* changes as the shopper answers.
   */
  readBranching() {
    this.stepRules = this.steps.map((step) => this.readRules(step));

    // groupId -> index of the step it lives in, so a skipped step's answers can
    // be left out of matching and its facet hidden on the result screen.
    this.stepOfGroup = new Map();
    this.steps.forEach((step, index) => {
      step.querySelectorAll(".ccq__group").forEach((group) => {
        this.stepOfGroup.set(group.dataset.groupId, index);
      });
    });

    // Every step is on the path until the first computeVisibility() says
    // otherwise — the helpers below read this, and restoreFromUrl() runs first.
    this.visible = this.steps.map(() => true);

    if (this.isDesignMode && this.stepRules[0].length > 0) {
      console.warn(
        "[configurator-quiz] the first question's visible_when rules are ignored — no question comes before it, so there are no answers to test them against.",
      );
    }
  }

  /**
   * One step's `visible_when` rules. A malformed attribute is reported and
   * treated as "no rules": a merchant typo should cost the branch, not the
   * whole quiz.
   */
  readRules(step) {
    const raw = step.dataset.visibleWhen;
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed
        .map((rule) => {
          const match = normalizeMatch(rule.match);
          if (!match.known && this.isDesignMode) {
            console.warn(
              "[configurator-quiz] visible_when on step",
              step.dataset.questionId,
              "has an unrecognised (translated?) match value",
              JSON.stringify(rule.match),
              '— reading it as "any". Clear the translation of the condition\'s `match` field.',
            );
          }
          return {
            tags: (rule.tags || []).map(normalizeTagId),
            match: match.value,
            negate: Boolean(rule.negate),
          };
        })
        .filter((rule) => rule.tags.length > 0);
    } catch (error) {
      console.error("[configurator-quiz] unreadable visible_when", raw, error);
      return [];
    }
  }

  /**
   * Decide which steps this shopper walks, from the answers alone.
   *
   * One forward pass: a step is judged against the answers of the *visible*
   * steps before it, and only then contributes its own. That keeps the rules
   * non-circular — a question can never depend on an answer given after it —
   * and means an answer left behind inside a skipped step cannot keep a later
   * step alive. Because the path is a pure function of the answer set, it can
   * be recomputed at any time, which is what lets the result screen's facets
   * re-plan the quiz without replaying it.
   *
   * The first step is always asked: nothing precedes it, so a rule there could
   * only ever be tested against an empty answer set.
   */
  computeVisibility() {
    const answered = new Set();
    this.visible = this.steps.map((step, index) => {
      const rules = this.stepRules[index];
      const show =
        index === 0 ||
        rules.length === 0 ||
        rules.some((rule) => this.ruleHolds(rule, answered));
      if (show) this.valuesIn(index).forEach((value) => answered.add(value));
      return show;
    });
  }

  /** Deep copy the mutable answer state before a user action. */
  captureSelectionState() {
    return {
      selections: new Map(
        Array.from(this.selections, ([groupId, values]) => [
          groupId,
          new Set(values),
        ]),
      ),
      priorities: new Map(this.selectionPriorities),
      interactionPriority: this.interactionPriority,
      seeded: new Set(this.seeded),
    };
  }

  restoreSelectionState(state) {
    this.selections = state.selections;
    this.selectionPriorities = state.priorities;
    this.interactionPriority = state.interactionPriority;
    this.seeded = state.seeded;
  }

  cloneSelections(selections = this.selections) {
    return new Map(
      Array.from(selections, ([groupId, values]) => [groupId, new Set(values)]),
    );
  }

  /**
   * Every violated Conditional tags rule as the exact selected source and
   * target values that disagree. Rules still use their original direction,
   * but resolution below may change either side so the newest click can win.
   */
  conditionalConflicts(selections = this.selections) {
    const conflicts = [];

    this.conditionalByGroup.forEach((rules, targetGroupId) => {
      if (!this.isGroupActive(targetGroupId)) return;
      const targetValues = selections.get(targetGroupId) || EMPTY_SET;
      if (targetValues.size === 0) return;

      rules.forEach((rule) => {
        selections.forEach((sourceValues, sourceGroupId) => {
          if (
            sourceGroupId === targetGroupId ||
            !this.isGroupActive(sourceGroupId)
          ) {
            return;
          }

          sourceValues.forEach((sourceValue) => {
            const handles = this.handlesOfValue.get(sourceValue) || EMPTY_SET;
            if (!handles.has(rule.key)) return;
            targetValues.forEach((targetValue) => {
              if (rule.values.has(targetValue)) return;
              conflicts.push({
                sourceGroupId,
                sourceValue,
                targetGroupId,
                targetValue,
              });
            });
          });
        });
      });
    });

    return conflicts;
  }

  /** Whether two snapshots hold exactly the same values for one group. */
  selectionSetsEqual(left = EMPTY_SET, right = EMPTY_SET) {
    if (left.size !== right.size) return false;
    for (const value of left) {
      if (!right.has(value)) return false;
    }
    return true;
  }

  /**
   * Complete every required group from the choices already fixed in
   * `selections`. Options are tried in their Liquid/Metaobject order. Trying a
   * whole branch rather than demanding that one refill be immediately valid is
   * what lets 230 V choose Controller and then continue on to Unterputz.
   */
  findConditionalCompletion(selections, requiredGroupIds, excluded = null) {
    if (this.conditionalConflicts(selections).length > 0) return null;

    const groups = Array.from(this.querySelectorAll(".ccq__group"));
    const missing = groups.find((group) => {
      const groupId = group.dataset.groupId;
      return (
        requiredGroupIds.has(groupId) &&
        this.isGroupActive(groupId) &&
        (selections.get(groupId) || EMPTY_SET).size === 0
      );
    });
    if (!missing) return selections;

    const groupId = missing.dataset.groupId;
    for (const option of missing.querySelectorAll(".ccq__opt")) {
      if (
        excluded?.groupId === groupId &&
        excluded.value === option.dataset.value
      ) {
        continue;
      }

      const trial = this.cloneSelections(selections);
      trial.get(groupId).add(option.dataset.value);
      const completed = this.findConditionalCompletion(
        trial,
        requiredGroupIds,
        excluded,
      );
      if (completed) return completed;
    }

    return null;
  }

  /**
   * Resolve Conditional tags as one transaction. Locks and the latest user
   * intent are hard constraints. Every other current answer is a soft
   * constraint, retained from newest to oldest when a complete valid answer
   * set still exists. Required gaps are then filled in Metaobject order.
   */
  resolveConditionalSelections(intent = null) {
    const adjusted = new Set();
    const groups = Array.from(this.querySelectorAll(".ccq__group"));
    const current = this.cloneSelections();
    const base = this.cloneSelections();
    const requiredGroupIds = new Set();

    groups.forEach((group) => {
      const groupId = group.dataset.groupId;
      if (!this.isGroupActive(groupId)) return;

      const selected = base.get(groupId);
      selected.clear();
      this.lockedIn(groupId).forEach((value) => selected.add(value));

      if (
        group.dataset.required === "true" &&
        ((current.get(groupId) || EMPTY_SET).size > 0 ||
          !this.seeded.has(groupId) ||
          intent?.groupId === groupId)
      ) {
        requiredGroupIds.add(groupId);
      }
    });

    const excluded =
      intent && intent.selected === false
        ? { groupId: intent.groupId, value: intent.value }
        : null;
    if (intent?.selected) {
      const selected = base.get(intent.groupId);
      if (!selected) return { adjusted, changed: false, valid: false };
      selected.add(intent.value);
    }

    // Locks and the click itself must have at least one possible completion.
    if (!this.findConditionalCompletion(base, requiredGroupIds, excluded)) {
      return { adjusted, changed: false, valid: false };
    }

    const preferences = [];
    groups.forEach((group, index) => {
      const groupId = group.dataset.groupId;
      if (!this.isGroupActive(groupId)) return;
      const locked = this.lockedIn(groupId);
      const optionOrder = new Map(
        Array.from(
          group.querySelectorAll(".ccq__opt"),
          (option, optionIndex) => [option.dataset.value, optionIndex],
        ),
      );

      (current.get(groupId) || EMPTY_SET).forEach((value) => {
        if (locked.has(value)) return;
        if (
          intent?.selected &&
          intent.groupId === groupId &&
          intent.value === value
        ) {
          return;
        }
        if (excluded?.groupId === groupId && excluded.value === value) {
          return;
        }
        preferences.push({
          group,
          groupId,
          value,
          priority: this.selectionPriorities.get(groupId) || 0,
          groupIndex: index,
          optionIndex: optionOrder.get(value) ?? Number.MAX_SAFE_INTEGER,
        });
      });
    });
    preferences.sort(
      (left, right) =>
        right.priority - left.priority ||
        left.groupIndex - right.groupIndex ||
        left.optionIndex - right.optionIndex,
    );

    let preferred = base;
    preferences.forEach((preference) => {
      const selected = preferred.get(preference.groupId);
      if (!selected || selected.has(preference.value)) return;

      // A shopper-selected value may sit beside pinned defaults, but two
      // shopper values may not occupy a single-choice group.
      if (preference.group.dataset.type !== "multi_choice") {
        const locked = this.lockedIn(preference.groupId);
        if (Array.from(selected).some((value) => !locked.has(value))) return;
      }

      const trial = this.cloneSelections(preferred);
      trial.get(preference.groupId).add(preference.value);
      if (this.findConditionalCompletion(trial, requiredGroupIds, excluded)) {
        preferred = trial;
      }
    });

    const completed = this.findConditionalCompletion(
      preferred,
      requiredGroupIds,
      excluded,
    );
    if (!completed) return { adjusted, changed: false, valid: false };

    let changed = false;
    groups.forEach((group) => {
      const groupId = group.dataset.groupId;
      const before = current.get(groupId) || EMPTY_SET;
      const after = completed.get(groupId) || EMPTY_SET;
      if (!this.selectionSetsEqual(before, after)) {
        changed = true;
        if (intent?.groupId !== groupId && before.size > 0) {
          adjusted.add(groupId);
        }
        if (intent?.groupId !== groupId) {
          this.selectionPriorities.set(groupId, 0);
        }
      }

      if (
        group.dataset.required === "true" &&
        this.isGroupActive(groupId) &&
        requiredGroupIds.has(groupId) &&
        after.size > 0
      ) {
        this.seeded.add(groupId);
      }
    });

    this.selections = completed;
    return { adjusted, changed, valid: true };
  }

  /** Describe values removed by conflict resolution for the shopper message. */
  adjustedOptions(before, adjustedGroupIds) {
    const removed = [];
    adjustedGroupIds.forEach((groupId) => {
      const group = this.querySelector(
        `.ccq__group[data-group-id="${groupId}"]`,
      );
      if (!group) return;
      const after = this.selections.get(groupId) || EMPTY_SET;
      (before.get(groupId) || EMPTY_SET).forEach((value) => {
        if (after.has(value)) return;
        const option = group.querySelector(`.ccq__opt[data-value="${value}"]`);
        removed.push({ label: option?.dataset.label || value });
      });
    });
    return removed;
  }

  /**
   * Carry the shopper's answer across a branch switch. When a re-plan takes one
   * group off the path and brings another onto it — a colour answer trading
   * one Bedienelemente question for its twin — the arriving group takes the
   * option equivalent to what the shopper last saw in the leaving one: the
   * same tag, or else the same `connected` product mappings.
   *
   * The leaving group always wins, so an answer the arriving group still holds
   * from an earlier switch is replaced. The carried value counts as the
   * shopper's own: it inherits the source's priority and seeds the group.
   * Without an equivalent the group is left alone and the usual first-option
   * fill applies.
   *
   * Returns the ids of the groups that received an answer.
   */
  carryAcrossBranch(wasVisible) {
    const carried = new Set();
    const wasActive = (groupId) => {
      const step = this.stepOfGroup.get(groupId);
      return step === undefined || wasVisible[step];
    };
    const priority = (group) =>
      this.selectionPriorities.get(group.dataset.groupId) || 0;

    const groups = Array.from(this.querySelectorAll(".ccq__group"));
    const leaving = groups
      .filter((group) => {
        const groupId = group.dataset.groupId;
        return (
          wasActive(groupId) &&
          !this.isGroupActive(groupId) &&
          this.hasOwnSelection(groupId)
        );
      })
      // Newest answer first: the one the shopper touched last is carried.
      .sort((left, right) => priority(right) - priority(left));
    if (leaving.length === 0) return carried;

    groups.forEach((group) => {
      const groupId = group.dataset.groupId;
      if (wasActive(groupId) || !this.isGroupActive(groupId)) return;

      for (const source of leaving) {
        const values = this.equivalentValues(source, group);
        if (values.length === 0) continue;

        this.clearGroup(groupId);
        const selected = this.selections.get(groupId);
        (group.dataset.type === "multi_choice"
          ? values
          : values.slice(0, 1)
        ).forEach((value) => selected.add(value));
        this.selectionPriorities.set(groupId, priority(source));
        this.seeded.add(groupId);
        carried.add(groupId);
        break;
      }
    });
    return carried;
  }

  /**
   * The options of `target` equivalent to the shopper's own answers in
   * `source`, in the order of those answers. The same tag is the closest
   * match; otherwise the first option whose `connected` mappings are the same.
   */
  equivalentValues(source, target) {
    const sourceLocked = this.lockedIn(source.dataset.groupId);
    const targetLocked = this.lockedIn(target.dataset.groupId);
    const options = Array.from(target.querySelectorAll(".ccq__opt")).filter(
      (option) => !targetLocked.has(option.dataset.value),
    );

    const values = [];
    this.selections.get(source.dataset.groupId).forEach((value) => {
      if (sourceLocked.has(value)) return;
      const signature = this.connectionSignature(value);
      const match =
        options.find((option) => option.dataset.value === value) ||
        (signature &&
          options.find(
            (option) =>
              this.connectionSignature(option.dataset.value) === signature,
          ));
      if (match && !values.includes(match.dataset.value)) {
        values.push(match.dataset.value);
      }
    });
    return values;
  }

  /** One tag's `connected` mappings as a comparable string; "" without any. */
  connectionSignature(tagId) {
    const matchers = this.tagConnections.get(normalizeTagId(tagId));
    if (!matchers) return "";
    return matchers
      .map(({ namespace, key, value }) => `${namespace}.${key}=${value}`)
      .sort()
      .join("\n");
  }

  /**
   * Re-derive the branch and settle Conditional tags to a fixed point. A
   * selection change can alter visible_when, which changes which answers may
   * constrain another group, so both calculations repeat together.
   */
  plan(intent = null) {
    const startingState = this.captureSelectionState();
    const adjusted = new Set();
    const carried = new Set();
    for (let pass = 0; pass < 12; pass++) {
      const wasVisible = this.visible;
      const previousVisibility = JSON.stringify(wasVisible);
      this.computeVisibility();
      // Before the required gaps are filled, so a carried answer is kept
      // rather than overwritten with the arriving group's first option.
      this.carryAcrossBranch(wasVisible).forEach((groupId) =>
        carried.add(groupId),
      );
      const resolved = this.resolveConditionalSelections(intent);
      resolved.adjusted.forEach((groupId) => adjusted.add(groupId));
      if (!resolved.valid) {
        this.restoreSelectionState(startingState);
        this.computeVisibility();
        this.computeConditionalPresentation();
        if (this.isDesignMode) {
          console.warn(
            "[configurator-quiz] conditional_tags has no compatible answer set; the previous selection was kept.",
          );
        }
        return { adjusted, valid: false };
      }
      if (
        !resolved.changed &&
        previousVisibility === JSON.stringify(this.visible)
      ) {
        // A carried answer replaced nothing the shopper could see, so it is
        // no adjustment to report.
        carried.forEach((groupId) => adjusted.delete(groupId));
        this.computeConditionalPresentation();
        return {
          adjusted,
          adjustedOptions: this.adjustedOptions(
            startingState.selections,
            adjusted,
          ),
          valid: true,
        };
      }
    }

    if (this.isDesignMode) {
      console.warn(
        "[configurator-quiz] the conditional_tags rules did not settle — check for groups whose rules point back at each other.",
      );
    }
    this.restoreSelectionState(startingState);
    this.computeVisibility();
    this.computeConditionalPresentation();
    return { adjusted, valid: false };
  }

  /** Mark direct conflicts without disabling or removing their controls. */
  computeConditionalPresentation() {
    const conflictsByGroup = new Map();
    this.querySelectorAll(".ccq__group").forEach((group) => {
      const groupId = group.dataset.groupId;
      const conflicts = new Set();
      group.querySelectorAll(".ccq__opt").forEach((option) => {
        const selected = this.selections.get(groupId);
        if (selected.has(option.dataset.value)) return;

        const trial = this.cloneSelections();
        if (group.dataset.type !== "multi_choice") {
          trial.get(groupId).clear();
          this.lockedIn(groupId).forEach((value) =>
            trial.get(groupId).add(value),
          );
        }
        trial.get(groupId).add(option.dataset.value);
        if (this.conditionalConflicts(trial).length > 0) {
          conflicts.add(option.dataset.value);
        }
      });
      if (conflicts.size > 0) conflictsByGroup.set(groupId, conflicts);
    });

    this.conflictingOptions = conflictsByGroup;
    this.applyConditionalPresentation();
  }

  applyConditionalPresentation() {
    this.querySelectorAll(".ccq__group").forEach((group) => {
      const groupId = group.dataset.groupId;
      const conflicts = this.conflictingOptions.get(groupId) || EMPTY_SET;
      group.querySelectorAll(".ccq__opt").forEach((option) => {
        const isConflict = conflicts.has(option.dataset.value);
        option.classList.toggle("ccq__opt--conflict", isConflict);
        if (isConflict && this.adjustsSelectionLabel) {
          option.setAttribute(
            "aria-label",
            `${option.dataset.label}. ${this.adjustsSelectionLabel}`,
          );
          option.dataset.conflictAria = "true";
        } else if (option.dataset.conflictAria === "true") {
          option.removeAttribute("aria-label");
          delete option.dataset.conflictAria;
        }
      });

      if (!this.facetsEl) return;
      this.facetsEl
        .querySelectorAll(`.ccq__facet-input[data-group-id="${groupId}"]`)
        .forEach((input) => {
          const isConflict = conflicts.has(input.value);
          const item = input.closest(".ccq__facet-item");
          item.classList.toggle("ccq__facet-item--conflict", isConflict);
          if (isConflict && this.adjustsSelectionLabel) {
            const label = item
              .querySelector(".ccq__facet-value")
              ?.textContent.trim();
            input.setAttribute(
              "aria-label",
              `${label || input.value}. ${this.adjustsSelectionLabel}`,
            );
            input.dataset.conflictAria = "true";
          } else if (input.dataset.conflictAria === "true") {
            input.removeAttribute("aria-label");
            delete input.dataset.conflictAria;
          }
        });
    });
  }

  /** OR over the rule's tags by default; `match: "all"` makes it AND. */
  ruleHolds(rule, answered) {
    const hit =
      rule.match === "all"
        ? rule.tags.every((tag) => answered.has(tag))
        : rule.tags.some((tag) => answered.has(tag));
    return rule.negate ? !hit : hit;
  }

  /** Every tag id selected inside one step, normalised for comparison. */
  valuesIn(index) {
    const values = [];
    this.groupsIn(index).forEach((group) => {
      this.selections
        .get(group.dataset.groupId)
        .forEach((value) => values.push(normalizeTagId(value)));
    });
    return values;
  }

  /** The steps the current answers put on the shopper's path, in order. */
  get path() {
    const path = [];
    this.visible.forEach((isVisible, index) => {
      if (isVisible) path.push(index);
    });
    return path;
  }

  /** Next / previous step on the path, or -1 when there is none. */
  nextIndex(from = this.current) {
    for (let index = from + 1; index < this.steps.length; index++) {
      if (this.visible[index]) return index;
    }
    return -1;
  }

  prevIndex(from = this.current) {
    for (let index = from - 1; index >= 0; index--) {
      if (this.visible[index]) return index;
    }
    return -1;
  }

  /**
   * Clamp to a step the shopper is meant to see. Reached from a `?ccq_step=`
   * that names a question this answer set skips (a link shared from another
   * branch, or a quiz edited since), where landing forward is the better guess
   * — the shopper was on their way through.
   */
  nearestVisible(index) {
    const clamped = Math.min(Math.max(index, 0), this.steps.length - 1);
    if (this.visible[clamped]) return clamped;
    const forward = this.nextIndex(clamped);
    if (forward !== -1) return forward;
    const back = this.prevIndex(clamped);
    return back === -1 ? 0 : back;
  }

  /** One progress segment per rendered step; the JS owns the count. */
  buildProgressSegments() {
    if (!this.barEl) return;
    this.barEl.textContent = "";
    this.segments = this.steps.map(() => {
      const segment = document.createElement("span");
      segment.className = "ccq__bar-seg";
      this.barEl.appendChild(segment);
      return segment;
    });
  }

  /**
   * Apply `?ccq=` / `?ccq_step=` to the quiz. Returns true when the URL asks for
   * the result screen. Unknown ids are ignored rather than treated as an error:
   * a link shared before the merchant edited the quiz should still open, just
   * without the answers that no longer exist.
   */
  restoreFromUrl() {
    const params = new URLSearchParams(window.location.search);

    const raw = params.get(PARAM_TAGS);
    const wanted = new Set(
      raw
        ? raw
            .split(",")
            .map((value) => value.trim())
            .filter(Boolean)
        : [],
    );

    if (wanted.size > 0) {
      this.querySelectorAll(".ccq__group").forEach((group) => {
        const selected = this.selections.get(group.dataset.groupId);
        const isSingle = group.dataset.type !== "multi_choice";
        group.querySelectorAll(".ccq__opt").forEach((option) => {
          if (!wanted.has(option.dataset.value)) return;
          // A hand-edited URL could name two values for a single-choice group;
          // keeping the first preserves the radio invariant the facets rely on.
          // Pinned answers sit outside that invariant — they are always there,
          // so counting them would leave no room for the shopper's own answer.
          if (isSingle && this.hasOwnSelection(group.dataset.groupId)) return;
          selected.add(option.dataset.value);
        });
      });
      this.querySelectorAll(".ccq__group").forEach((group) =>
        this.syncGroup(group),
      );
    }

    const view = params.get(PARAM_VIEW);
    if (view === VIEW_RESULTS) {
      // The result screen with no answers in the link is a state the shopper
      // asked for — the sidebar's reset writes exactly this. Count the required
      // groups as already pre-answered so the first plan() does not fill them
      // back in with their first option and quietly undo the reset.
      if (wanted.size === 0) {
        this.querySelectorAll('.ccq__group[data-required="true"]').forEach(
          (group) => this.seeded.add(group.dataset.groupId),
        );
      }
      return true;
    }

    const step = parseInt(view, 10);
    if (Number.isFinite(step)) {
      // The restored answers decide the path, so plan it before honouring the
      // step — the URL may name a question this branch skips.
      this.plan();
      this.current = this.nearestVisible(step - 1);
    }
    return false;
  }

  /**
   * Write the current answers and screen back to the URL, leaving every other
   * query param untouched.
   *
   * replaceState rather than pushState (which is what the theme's facets.js
   * uses): the quiz is a single screen, so Back should leave the page instead
   * of walking backwards through every option a shopper tried on the way.
   */
  syncUrl() {
    // Only the answers on the shopper's current path: a value left behind in a
    // step this branch skips is already out of the matching set, and writing it
    // to the URL would hand a shared link an answer the quiz never asked for.
    // Pinned answers are left out too: they come from the quiz, not from the
    // shopper, so the link stays clean on an untouched quiz — and a group whose
    // defaults a merchant has since changed picks up the new ones on open
    // rather than the ones baked into the link.
    const values = [];
    this.selections.forEach((selected, groupId) => {
      if (!this.isGroupActive(groupId)) return;
      const locked = this.lockedIn(groupId);
      selected.forEach((value) => {
        if (!locked.has(value)) values.push(value);
      });
    });
    const view = this.result.hidden ? String(this.current + 1) : VIEW_RESULTS;

    const url = new URL(window.location.href);
    if (values.length > 0) url.searchParams.set(PARAM_TAGS, values.join(","));
    else url.searchParams.delete(PARAM_TAGS);

    // An untouched quiz on step 1 is the default state — leave the URL clean.
    if (values.length > 0 || view !== "1") {
      url.searchParams.set(PARAM_VIEW, view);
    } else {
      url.searchParams.delete(PARAM_VIEW);
    }

    window.history.replaceState(window.history.state, "", url);
  }

  /**
   * A facet input mirrors one quiz option. Radios (required groups) replace the
   * group's answer; checkboxes (optional groups) toggle within it. Either way
   * the quiz's own buttons and the product list follow along.
   */
  onFacetChange = (event) => {
    const input = event.target.closest(".ccq__facet-input");
    if (!input) return;

    const groupId = input.dataset.groupId;
    const selected = this.selections.get(groupId);
    if (!selected) return;
    const previousState = this.captureSelectionState();
    const shouldSelect = input.type === "radio" || input.checked;

    if (input.type === "radio") {
      this.clearGroup(groupId);
      selected.add(input.value);
    } else if (input.checked) {
      selected.add(input.value);
    } else {
      selected.delete(input.value);
    }

    this.interactionPriority += 1;
    this.selectionPriorities.set(groupId, this.interactionPriority);
    const resolution = this.plan({
      groupId,
      value: input.value,
      selected: shouldSelect,
    });
    if (
      !resolution.valid ||
      this.selections.get(groupId).has(input.value) !== shouldSelect
    ) {
      this.restoreSelectionState(previousState);
      this.plan();
      this.syncAllGroups();
      this.warnUnresolvableSelection(groupId, input.value);
      return;
    }

    const group = this.querySelector(`.ccq__group[data-group-id="${groupId}"]`);
    if (group) {
      this.syncGroup(group);
      // Queued, not pushed: the event reports the count this change produced,
      // and that only exists once showProducts has re-matched the grid.
      this.tracking?.filterModify(
        group,
        input,
        input.type === "radio" ? "change" : input.checked ? "add" : "remove",
      );
    }
    this.syncAllGroups();
    this.syncFacetVisibility();
    this.syncNav();
    this.syncUrl();
    this.applyFilters();
    this.updateAdjustmentStatus(resolution, group, input.value);
  };

  /** A facet opened on a phone gets its layout only now. */
  onFacetToggle = (event) => {
    const facet = event.target;
    if (!facet.classList?.contains("ccq__facet") || !facet.open) return;
    this.revealCheckedFacetOption(facet.querySelector(".ccq__facet-list"));
  };

  onClick = (event) => {
    // Result-screen clicks are reported and then left alone — the click still
    // reaches the product link or the button it landed on.
    this.trackGridClick(event);
    this.trackCtaClick(event);

    const option = event.target.closest(".ccq__opt");
    if (!option || !this.contains(option)) return;
    // A pinned answer is inert: it is already selected and stays that way.
    if (option.dataset.locked === "true") return;

    const group = option.closest(".ccq__group");
    const groupId = group.dataset.groupId;
    const selected = this.selections.get(groupId);
    const value = option.dataset.value;
    const previousState = this.captureSelectionState();
    let shouldSelect;

    if (group.dataset.type === "multi_choice") {
      shouldSelect = !selected.has(value);
      if (shouldSelect) selected.add(value);
      else selected.delete(value);
    } else {
      // Single choice: clicking the active option clears it — except in a
      // required group, which always carries an answer (it opens on its first
      // option), so clearing there would only bounce the answer back to the
      // top of the list on the next plan.
      const wasSelected = selected.has(value);
      if (wasSelected && group.dataset.required === "true") return;
      shouldSelect = !wasSelected;
      this.clearGroup(groupId);
      if (!wasSelected) selected.add(value);
    }

    this.interactionPriority += 1;
    this.selectionPriorities.set(groupId, this.interactionPriority);
    const resolution = this.plan({ groupId, value, selected: shouldSelect });
    if (
      !resolution.valid ||
      this.selections.get(groupId).has(value) !== shouldSelect
    ) {
      this.restoreSelectionState(previousState);
      this.plan();
      this.syncAllGroups();
      this.warnUnresolvableSelection(groupId, value);
      return;
    }

    this.tracking?.start("option");
    this.tracking?.answer(group);
    this.warmProducts();

    this.syncGroup(group);
    // An answer can put later questions on or off the path, so repaint the
    // whole card — the progress bar's total is not a constant any more.
    this.render();
    this.syncUrl();
    this.updateAdjustmentStatus(resolution, group, value);
  };

  updateAdjustmentStatus(resolution, group, selectedValue) {
    this.hideAdjustmentStatus();
    if (resolution.adjustedOptions.length === 0) return;
    if (group?.dataset.showAdjustmentMessage === "false") {
      return;
    }
    const selectedOption = group?.querySelector(
      `.ccq__opt[data-value="${selectedValue}"]`,
    );
    this.showAdjustmentStatus(
      resolution.adjustedOptions,
      selectedOption?.dataset.label || selectedValue,
    );
  }

  showAdjustmentStatus(removedOptions, selectedLabel) {
    const removedLabels = removedOptions.map(({ label }) => label);
    const selectedText = `“${selectedLabel}”`;
    let removedText;
    try {
      removedText = new Intl.ListFormat(document.documentElement.lang, {
        style: "long",
        type: "conjunction",
      }).format(removedLabels);
    } catch (error) {
      removedText = removedLabels.join(", ");
    }
    const template =
      removedOptions.length === 1
        ? this.adjustmentSingle
        : this.adjustmentMultiple;
    const message = template
      .replaceAll("__REMOVED__", removedText)
      .replaceAll("__SELECTED__", selectedText);

    this.adjustmentStatusEls.forEach((status) => {
      const text = status.querySelector("[data-ccq-adjustment-status-text]");
      if (text) text.textContent = message;
      status.hidden = false;
    });
  }

  hideAdjustmentStatus() {
    this.adjustmentStatusEls.forEach((status) => {
      const text = status.querySelector("[data-ccq-adjustment-status-text]");
      if (text) text.textContent = "";
      status.hidden = true;
    });
  }

  warnUnresolvableSelection(groupId, value) {
    if (!this.isDesignMode) return;
    console.warn(
      "[configurator-quiz] conditional_tags cannot resolve the selected option because the remaining configuration is locked or has no compatible required answer:",
      groupId,
      value,
    );
  }

  /**
   * select_item for a product clicked in the result grid. The handle in the
   * card's href is what ties the card back to the matched product — the cards
   * are the theme's own markup, so there is nothing else in them to key on.
   */
  trackGridClick(event) {
    if (!this.tracking || !this.matched) return;
    const link = event.target.closest("a[href]");
    if (!link || !this.gridEl.contains(link)) return;

    const match = link.getAttribute("href").match(/\/products\/([^/?#]+)/);
    if (!match) return;
    const handle = decodeURIComponent(match[1]);
    const index = this.matched.findIndex(
      (product) => product.handle === handle,
    );
    if (index === -1) return;

    this.tracking.selectItem(this.matched[index], index);
  }

  /**
   * quiz_cta_click for any [data-ccq-cta] in the quiz — load more, reset
   * filters, and whatever a merchant adds to the result screen later. Restart
   * is not one of them: it has an event of its own.
   */
  trackCtaClick(event) {
    if (!this.tracking) return;
    const cta = event.target.closest("[data-ccq-cta]");
    if (cta && this.contains(cta)) this.tracking.cta(cta);
  }

  /** Repaint every group's options and facet inputs from the selections. */
  syncAllGroups() {
    this.querySelectorAll(".ccq__group").forEach((group) =>
      this.syncGroup(group),
    );
  }

  syncGroup(group) {
    const groupId = group.dataset.groupId;
    const selected = this.selections.get(groupId);
    group.querySelectorAll(".ccq__opt").forEach((option) => {
      const isSelected = selected.has(option.dataset.value);
      option.classList.toggle("ccq__opt--selected", isSelected);
      option.setAttribute("aria-pressed", isSelected ? "true" : "false");
    });
    this.syncFacet(groupId);
  }

  /** Mirror one group's selection onto its facet inputs in the sidebar. */
  syncFacet(groupId) {
    if (!this.facetsEl) return;
    const selected = this.selections.get(groupId);
    let changed = false;
    this.facetsEl
      .querySelectorAll(`.ccq__facet-input[data-group-id="${groupId}"]`)
      .forEach((input) => {
        const checked = selected.has(input.value);
        if (input.checked !== checked) changed = true;
        input.checked = checked;
      });
    // Only a change made here came from outside the list — a conflict
    // resolution or a carried answer. The shopper's own click has already
    // set the input, and must not make the list jump.
    if (changed) {
      this.revealCheckedFacetOption(
        this.facetsEl.querySelector(
          `.ccq__facet[data-facet-for="${groupId}"] .ccq__facet-list`,
        ),
      );
    }
    this.syncFacetsReset();
  }

  /**
   * Scroll a facet list so its checked option sits fully inside the list's
   * box. Only the list scrolls: scrollIntoView() would move the page too. A
   * list with any of the shopper's checked options already in full view is
   * left alone, and one without layout yet — result screen hidden, facet
   * closed or skipped — is revealed again once it gets one.
   */
  revealCheckedFacetOption(list) {
    if (!list || list.clientHeight === 0) return;
    this.fitCheckedFacetOption(list);
    // Remembered so a later reflow can tell whether the shopper has scrolled
    // the list since.
    this.facetListScrollTops.set(list, list.scrollTop);
  }

  /**
   * The lists settle after they are revealed: the counts arrive with the
   * products and can wrap a label onto a second line, pushing the checked
   * option back below the fold. A list still where the last reveal left it is
   * revealed again; one the shopper has scrolled is theirs.
   */
  onFacetItemResize = (entries) => {
    const lists = new Set(
      entries.map((entry) => entry.target.closest(".ccq__facet-list")),
    );
    lists.forEach((list) => {
      if (
        this.facetListScrollTops.has(list) &&
        this.facetListScrollTops.get(list) === list.scrollTop
      ) {
        this.revealCheckedFacetOption(list);
      }
    });
  };

  fitCheckedFacetOption(list) {
    if (list.scrollHeight <= list.clientHeight) return;

    const checked = Array.from(
      list.querySelectorAll(".ccq__facet-input:checked"),
    ).filter((input) => !input.closest(".ccq__facet-item").hidden);
    // Pinned answers are checked too, but the shopper looks for their own.
    const own = checked.filter((input) => !input.disabled);
    const items = (own.length > 0 ? own : checked).map((input) =>
      input.closest(".ccq__facet-item"),
    );
    if (items.length === 0) return;

    const box = list.getBoundingClientRect();
    const top = box.top + list.clientTop;
    const bottom = top + list.clientHeight;
    const rects = items.map((item) => item.getBoundingClientRect());
    // A pixel of slack for sub-pixel rounding.
    const inView = (rect) => rect.top >= top - 1 && rect.bottom <= bottom + 1;
    if (rects.some(inView)) return;

    const rect = rects[0];
    list.scrollTop +=
      rect.top < top || rect.height > list.clientHeight
        ? rect.top - top
        : rect.bottom - bottom;
  }

  /**
   * Hide the sidebar facets of the questions this shopper's answers skip. They
   * were never asked, their answers are already out of the matching set, and
   * leaving them on screen would invite an answer to a question the quiz has
   * decided is irrelevant.
   */
  syncFacetVisibility() {
    if (!this.facetsEl) return;
    this.facetsEl.querySelectorAll(".ccq__facet").forEach((facet) => {
      const wasHidden = facet.hidden;
      facet.hidden = !this.isGroupActive(facet.dataset.facetFor);
      // A facet arriving with a branch switch was checked while it had no
      // layout, so its answer is revealed now.
      if (wasHidden && !facet.hidden) {
        this.revealCheckedFacetOption(facet.querySelector(".ccq__facet-list"));
      }
    });
    this.syncFacetsReset();
  }

  /**
   * False for a group whose step is off the current visible_when branch. A
   * Conditional tags conflict never deactivates a group anymore.
   */
  isGroupActive(groupId) {
    const step = this.stepOfGroup.get(groupId);
    return step === undefined || this.visible[step];
  }

  /** The reset link only makes sense while some group holds an answer. */
  syncFacetsReset() {
    if (!this.facetsResetBtn) return;
    const hasAnswer = Array.from(this.querySelectorAll(".ccq__group")).some(
      (group) =>
        this.isGroupActive(group.dataset.groupId) &&
        // Pins survive a reset, so a group holding nothing else has nothing to
        // reset — offering the link would promise a change that never happens.
        this.hasOwnSelection(group.dataset.groupId),
    );
    this.facetsResetBtn.hidden = !hasAnswer;
  }

  /**
   * Clear every answer the shopper gave, required groups included. Only the
   * merchant's pinned answers survive and continue to filter the grid.
   *
   * A required group left empty stays empty: the pre-answering that opens a
   * required question on its first option must not undo the reset the moment
   * the path is re-planned, so every required group counts as seeded here.
   */
  resetFacets() {
    this.interactionPriority = 0;
    this.querySelectorAll(".ccq__group").forEach((group) => {
      const groupId = group.dataset.groupId;
      this.clearGroup(groupId);
      this.selectionPriorities.set(groupId, 0);
      if (group.dataset.required === "true") {
        this.seeded.add(groupId);
      }
      this.syncGroup(group);
    });
    this.hideAdjustmentStatus();
    // Clearing an answer can be what a later question hung on.
    this.plan();
    this.syncAllGroups();
    this.syncFacetVisibility();
    this.syncNav();
    this.syncUrl();
    this.applyFilters();
  }

  /**
   * A step is answered once every required group in it has a selection.
   */
  canAdvance(index = this.current) {
    return this.groupsIn(index)
      .filter(
        (group) =>
          group.dataset.required === "true" &&
          this.isGroupActive(group.dataset.groupId),
      )
      .every((group) => this.selections.get(group.dataset.groupId).size > 0);
  }

  groupsIn(index) {
    return Array.from(this.steps[index].querySelectorAll(".ccq__group"));
  }

  onNext() {
    if (!this.canAdvance()) return;
    this.tracking?.start("next");
    // A multi-choice group is only settled when the shopper leaves the step,
    // which is here.
    this.tracking?.submitStep(this.current);
    this.advance();
  }

  onSkip() {
    this.tracking?.start("skip");
    this.tracking?.skip(this.current);
    this.groupsIn(this.current).forEach((group) => {
      const groupId = group.dataset.groupId;
      this.clearGroup(groupId);
      this.selectionPriorities.set(groupId, 0);
      this.syncGroup(group);
    });
    this.advance();
  }

  /**
   * Move to the next question this shopper's answers actually reach, or finish
   * when there is none left. The answer that decides the branch is the one just
   * given, so the path is re-planned before it is read.
   */
  advance() {
    this.warmProducts();
    this.plan();
    const next = this.nextIndex();
    if (next === -1) this.finish();
    else this.go(next);
  }

  goBack() {
    const previous = this.prevIndex();
    if (previous === -1) return;
    this.tracking?.back(this.current, previous);
    this.go(previous);
  }

  go(index) {
    this.hideAdjustmentStatus();
    this.plan();
    this.current = this.nearestVisible(index);
    this.render();
    this.syncUrl();
    if (window.matchMedia("(max-width: 749px)").matches) this.alignProgress();
    else this.scrollIntoViewIfNeeded();
  }

  render() {
    this.plan();

    this.steps.forEach((step, index) => {
      // Only the current step shows. It is kept visible even if the rules would
      // now skip it — that cannot happen from its own answers (a rule only ever
      // reads earlier ones), and showing a blank card would be worse than
      // showing one question too many.
      step.hidden = index !== this.current;
    });

    const step = this.steps[this.current];
    const path = this.path;
    const position = path.indexOf(this.current) + 1;

    this.stepNum.textContent = position;
    this.stepTotal.textContent = path.length;
    this.stepName.textContent = step.dataset.stepName || "";

    const eyebrowNum = step.querySelector(".ccq__eyebrow-num");
    if (eyebrowNum) eyebrowNum.textContent = position;

    this.renderProgress(path);

    // Every group, not just this step's: planning can have replaced an answer
    // further along, and the result sidebar mirrors all of them.
    this.syncAllGroups();
    this.syncNav();
    // render() runs on every answer, not only on navigation — the tracker
    // drops the repeats and reports the step only when it actually changes.
    this.tracking?.questionView(this.current);
  }

  /**
   * Fill the segments up to and including the current step — the step you are
   * on counts as progress, so step 1 of 5 already reads 20%.
   *
   * A step a branch has taken away collapses to nothing instead of vanishing
   * outright, so the bar visibly re-plans. The alternative — a continuous fill
   * over a shrinking total — moves the bar and the "x von y" label at once with
   * nothing to explain why, which reads as a glitch.
   */
  renderProgress(path) {
    if (!this.segments) return;
    const position = path.indexOf(this.current);
    this.segments.forEach((segment, index) => {
      const onPath = this.visible[index];
      segment.classList.toggle("ccq__bar-seg--off", !onPath);
      segment.classList.toggle(
        "ccq__bar-seg--done",
        onPath && path.indexOf(index) <= position,
      );
    });
  }

  syncNav() {
    const isLast = this.nextIndex() === -1;
    const optional = this.groupsIn(this.current).every(
      (group) =>
        group.dataset.required !== "true" ||
        !this.isGroupActive(group.dataset.groupId),
    );

    this.backBtn.disabled = this.prevIndex() === -1;
    this.nextBtn.disabled = !this.canAdvance();
    this.nextBtn.textContent = isLast ? this.finishLabel : this.nextLabel;
    this.skipBtn.hidden = !optional;
  }

  finish({ scroll = true, restored = false } = {}) {
    this.hideAdjustmentStatus();
    if (this.intro) this.intro.hidden = true;
    this.card.hidden = true;
    this.result.hidden = false;
    this.plan();
    this.syncAllGroups();
    this.syncFacetVisibility();
    this.syncUrl();
    // The answer set is final here, before the products are fetched. A screen
    // restored from a shared link completed nothing — someone else's answers
    // arrived in the URL already finished.
    if (!restored) this.tracking?.complete();
    // The sidebar sits above the grid on narrow screens, where six open groups
    // would push the products off-screen — collapse them there. Decided on
    // reveal rather than on load so a rotate or resize is accounted for.
    if (this.facetsEl) {
      const collapsed = window.matchMedia("(max-width: 989px)").matches;
      this.querySelectorAll(".ccq__facet").forEach((facet) => {
        facet.open = !collapsed;
      });
      // The lists had no layout until now; a closed one waits for its toggle.
      this.facetsEl
        .querySelectorAll(".ccq__facet-list")
        .forEach((list) => this.revealCheckedFacetOption(list));
    }
    if (scroll) this.scrollIntoViewIfNeeded();

    // Kick off product matching; renders into the grid asynchronously.
    this.showProducts();
  }

  /**
   * The answered facets, as `{ id, values }` — one entry per group that holds
   * an answer the shopper gave. Ids are normalised to the trailing numeric
   * segment so DOM values (numeric) and Storefront API GIDs
   * (gid://shopify/Metaobject/NNNN) compare equal.
   */
  selectionGroups() {
    const groups = [];
    this.querySelectorAll(".ccq__group").forEach((groupEl) => {
      // A branch the shopper is no longer on keeps its answers in memory — so
      // walking back into it restores them — but they must not filter the grid:
      // a question that was never asked cannot narrow the result.
      if (!this.isGroupActive(groupEl.dataset.groupId)) return;
      const selected = this.selections.get(groupEl.dataset.groupId);
      if (!selected || selected.size === 0) return;
      // Pins are fallback filters: use them while the group has no answer from
      // the shopper, then let the shopper's own values replace them.
      const locked = this.lockedIn(groupEl.dataset.groupId);
      const ownValues = new Set();
      selected.forEach((value) => {
        if (!locked.has(value)) ownValues.add(normalizeTagId(value));
      });
      const values =
        ownValues.size > 0
          ? ownValues
          : new Set(Array.from(selected, normalizeTagId));
      if (values.size === 0) return;
      groups.push({ id: groupEl.dataset.groupId, values });
    });
    return groups;
  }

  /**
   * The configurator_tag ids a product satisfies: the ones its
   * custom.configurator_tags metafield lists, plus every tag whose `connected`
   * mapping the product's own metafields fulfil. Computed once per catalogue
   * read, since neither side changes while the shopper adjusts facets.
   */
  resolveMatchIds(product) {
    const ids = new Set(product.tagIds);
    this.tagConnections.forEach((matchers, tagId) => {
      if (ids.has(tagId)) return;
      if (matchers.some((matcher) => this.productHasValue(product, matcher))) {
        ids.add(tagId);
      }
    });
    return ids;
  }

  /** Does this product's metafield carry the value the mapping asks for? */
  productHasValue(product, { namespace, key, value }) {
    const values = product.fields.get(`${namespace}.${key}`);
    if (!values || values.size === 0) return false;
    // A mapping without "=value" only asks for the metafield to be set at all.
    return value ? values.has(value) : true;
  }

  /**
   * AND within a group (a product must carry *every* value the shopper picked
   * in that group), AND/OR across groups per the merchant's match-mode
   * setting. No selections → every product matches.
   */
  productMatches(productTagIds, selectionGroups) {
    if (selectionGroups.length === 0) return true;
    const inGroup = (group) => {
      for (const id of group.values) {
        if (!productTagIds.has(id)) return false;
      }
      return true;
    };
    return this.config.matchMode === "any"
      ? selectionGroups.some(inGroup)
      : selectionGroups.every(inGroup);
  }

  /**
   * Re-run matching against the already-fetched catalogue. Used when a facet in
   * the sidebar changes, so adjusting an answer never refetches anything — the
   * products and their tags cannot change between two clicks, and a full paged
   * read per click would both stall the grid and burn Storefront API quota.
   */
  applyFilters() {
    if (!this.products) return;
    this.showProducts();
  }

  /** Product filters after an unchecked facet is selected and conflicts settle. */
  selectionGroupsAfterFacet(input) {
    if (input.checked) return { valid: true, groups: this.selectionGroups() };

    const state = this.captureSelectionState();
    const groupId = input.dataset.groupId;
    const selected = this.selections.get(groupId);
    if (!selected) return { valid: false, groups: [] };

    if (input.type === "radio") this.clearGroup(groupId);
    selected.add(input.value);
    this.interactionPriority += 1;
    this.selectionPriorities.set(groupId, this.interactionPriority);

    const resolution = this.resolveConditionalSelections({
      groupId,
      value: input.value,
      selected: true,
    });
    const groups = resolution.valid ? this.selectionGroups() : [];
    this.restoreSelectionState(state);
    return { valid: resolution.valid, groups };
  }

  /**
   * For each facet option: how many products the grid would hold if it were
   * clicked, given the answers already given elsewhere. Zero-count options get
   * dimmed, the way the theme's collection filters do, so a dead end is
   * visible before it is clicked.
   */
  updateFacetCounts(selectionGroups) {
    if (!this.facetsEl || !this.products) return;

    this.facetsEl.querySelectorAll(".ccq__facet-input").forEach((input) => {
      if (!this.isGroupActive(input.dataset.groupId)) return;
      if (input.closest(".ccq__facet-item").hidden) return;
      const candidate = this.selectionGroupsAfterFacet(input);
      const count = candidate.valid
        ? this.products.reduce(
            (total, product) =>
              this.productMatches(product.matchIds, candidate.groups)
                ? total + 1
                : total,
            0,
          )
        : 0;

      const item = input.closest(".ccq__facet-item");
      item.querySelector(".ccq__facet-count").textContent = `(${count})`;
      // Never dim the answer currently in force — removing it is the only way
      // out of a zero-result selection.
      const isEmpty = count === 0 && !input.checked;
      item.classList.toggle("ccq__facet-item--empty", isEmpty);
    });
  }

  async showProducts() {
    // Each run supersedes the last: a shopper clicking through facets must not
    // have a slower earlier run append its cards on top of the current answer.
    const run = (this.run = (this.run || 0) + 1);
    const isStale = () => this.run !== run;

    this.gridEl.innerHTML = "";
    this.countEl.hidden = true;
    this.moreBtn.hidden = true;
    this.setStatus("");
    this.setLoading(true);

    try {
      this.products = await this.loadProducts();
    } catch (error) {
      console.error("[configurator-quiz] product fetch failed", error);
      if (isStale()) return;
      this.setLoading(false);
      this.setStatus(this.resultsEl.dataset.errorText, "error");
      return;
    }
    if (isStale()) return;

    const selectionGroups = this.selectionGroups();
    this.updateFacetCounts(selectionGroups);

    // The metafield is not filterable server-side, so we match here and
    // paginate the matched set into the grid (see renderNextPage).
    this.matched = this.products.filter((product) =>
      this.productMatches(product.matchIds, selectionGroups),
    );
    this.shown = 0;

    // One report per render of the grid, filter changes included: it flushes
    // the queued quiz_filter_modify and then reports the screen itself.
    this.tracking?.results(this.matched.length);

    if (this.matched.length === 0) {
      this.setLoading(false);
      this.setStatus(this.resultsEl.dataset.emptyText);
      return;
    }

    const countText = this.resultsEl.dataset.countText || "";
    this.countEl.textContent = `${this.matched.length} ${countText}`.trim();
    this.countEl.hidden = false;

    await this.renderNextPage(run);
  }

  /**
   * Render the next page of matched products into the grid. Each card is the
   * theme's real collection-product-card, fetched by handle through the Section
   * Rendering API and appended in the matched order.
   */
  async renderNextPage(run = this.run) {
    const page = this.matched.slice(
      this.shown,
      this.shown + this.config.pageSize,
    );
    this.shown += page.length;

    this.moreBtn.hidden = true;
    this.setLoading(true);

    // One marker per card, appended up front, so the cards can be requested in
    // parallel and still land in matched order — each one replaces its own
    // marker the moment it arrives, rather than the whole page appearing at
    // once behind the slowest request. Comments, not elements: the grid is a
    // flex column, and an empty wrapper would be a gap in it.
    const markers = page.map((product) => {
      const marker = document.createComment(`ccq:${product.handle}`);
      this.gridEl.appendChild(marker);
      return marker;
    });

    await Promise.all(
      page.map(async (product, index) => {
        const node = await this.renderCard(product);
        // A facet change during the fetch started a newer run — drop this card
        // rather than dropping it into a grid that has moved on. The markers
        // went with the grid this run cleared, so there is nothing to tidy.
        if (this.run !== run) return;
        if (node) markers[index].replaceWith(node);
        else markers[index].remove();
      }),
    );
    if (this.run !== run) return;

    this.setLoading(false);
    // Per page rather than per result: the item indexes then match the cards
    // that were actually put in front of the shopper.
    this.tracking?.viewItemList(page, this.shown - page.length);

    this.moreBtn.hidden = this.shown >= this.matched.length;
  }

  /** Toggle the spinner + skeleton placeholders that stand in for the cards. */
  setLoading(isLoading) {
    if (this.loaderEl) this.loaderEl.hidden = !isLoading;
    this.resultsEl.setAttribute("aria-busy", isLoading ? "true" : "false");
  }

  /**
   * One product's card, as a fresh document fragment ready to append. Returns
   * null when the card could not be rendered.
   */
  async renderCard(product) {
    const template = await this.cardTemplate(product);
    return template ? template.content.cloneNode(true) : null;
  }

  /**
   * The card markup for one product, as a <template> held for the rest of the
   * page view. A facet change re-runs the grid over a result set that mostly
   * overlaps the last one, and a card is fixed markup for a fixed handle — so
   * without this every click in the sidebar re-pays a full page of Section
   * Rendering requests to put the very same cards back. The promise is what is
   * cached, so two overlapping runs asking for the same handle share one
   * request; a failed render is not cached, leaving the next run free to retry.
   */
  cardTemplate(product) {
    const cached = this.cardCache.get(product.handle);
    if (cached) return cached;

    const pending = this.fetchCardTemplate(product).catch((error) => {
      console.error(
        "[configurator-quiz] card render failed",
        product.handle,
        error,
      );
      this.cardCache.delete(product.handle);
      return null;
    });
    this.cardCache.set(product.handle, pending);
    return pending;
  }

  /**
   * Fetch one product's card markup via the Section Rendering API and return it
   * as a <template> — unwrapped from the section container so the grid does not
   * collect duplicate `shopify-section` ids.
   */
  async fetchCardTemplate(product) {
    const root =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
      "/";
    const url = `${root}products/${product.handle}?section_id=${this.cardSection}`;
    const response = await fetch(url);
    if (!response.ok)
      throw new Error(`Section render responded ${response.status}`);
    const wrapper = document.createElement("template");
    wrapper.innerHTML = (await response.text()).trim();
    const section = wrapper.content.firstElementChild;
    if (!section) return null;
    const template = document.createElement("template");
    while (section.firstChild) template.content.appendChild(section.firstChild);
    return template;
  }

  setStatus(text, variant) {
    this.statusEl.textContent = text || "";
    this.statusEl.hidden = !text;
    this.statusEl.classList.toggle(
      "ccq__results-status--error",
      variant === "error",
    );
  }

  /**
   * Start the catalogue read while the shopper is still answering, so the
   * result screen is not where its latency is spent. Called from the first
   * answer and from every step forward — not from connectedCallback: a page
   * view that never reaches a question should not cost a paged Storefront
   * read. loadProducts memoises, so the calls after the first are free.
   *
   * The rejection is swallowed here and only here: loadProducts clears its
   * memo on failure, so the real finish() still fetches, still fails, and
   * still shows the error. Without the catch this would be an unhandled
   * rejection in the console for every shopper whose warm-up missed.
   *
   * One attempt per page view, for that same reason: a cleared memo would make
   * every later click start another read, so a Storefront API that is down
   * would be asked again on every answer instead of once, at finish, where the
   * shopper is actually waiting for it.
   */
  warmProducts() {
    if (this.warmed) return;
    this.warmed = true;
    this.loadProducts().catch(() => {});
  }

  /**
   * The collection's products with their tag ids, from the cheapest source that
   * has them: this element's in-memory promise, then localStorage, then the
   * Storefront API.
   */
  loadProducts() {
    if (!this._productsPromise) {
      const cached = this.readCachedProducts();
      const source = cached
        ? Promise.resolve(cached)
        : this.fetchAllProducts()
            .then((products) => {
              this.writeCachedProducts(products);
              return products;
            })
            .catch((error) => {
              this._productsPromise = null; // allow a retry on the next finish()
              throw error;
            });

      // Both sources carry raw metafield values; fold them into the tag ids the
      // matcher compares here, once per catalogue rather than per facet click.
      this._productsPromise = source.then((products) => {
        products.forEach((product) => {
          product.matchIds = this.resolveMatchIds(product);
        });
        return products;
      });
    }
    return this._productsPromise;
  }

  /**
   * One cache entry per catalogue read. Locale is part of the key because
   * @inContext changes the handles that come back, and a handle from the wrong
   * language only redirects when the card is fetched. The connected metafields
   * are in there too: an entry only holds the fields the quiz asked for at the
   * time, so adding a mapping must not read back the older, thinner entry.
   */
  get cacheKey() {
    const { shopDomain, collectionHandle, apiVersion, language, country } =
      this.config;
    const fields = hashString(
      this.metafieldIdentifiers
        .map(({ namespace, key }) => `${namespace}.${key}`)
        .sort()
        .join(","),
    );
    return `${CACHE_PREFIX}${shopDomain}:${collectionHandle}:${apiVersion}:${language}:${country}:${fields}`;
  }

  /** The cached catalogue, or null when it is absent, stale or unreadable. */
  readCachedProducts() {
    if (!this.config.cacheTtl || this.isDesignMode) return null;

    let raw = null;
    try {
      raw = window.localStorage.getItem(this.cacheKey);
    } catch (error) {
      return null; // Safari private mode and friends — just fetch.
    }
    if (!raw) return null;

    try {
      const entry = JSON.parse(raw);
      if (!entry || !Array.isArray(entry.products)) return null;
      // A negative age means the clock moved backwards since the write; treat
      // that as stale rather than as a cache entry that never expires.
      const age = Date.now() - entry.savedAt;
      if (!(age >= 0 && age < this.config.cacheTtl * 60000)) return null;
      return entry.products.map((product) => ({
        ...product,
        tagIds: new Set(product.tagIds),
        fields: new Map(
          (product.fields || []).map(([key, values]) => [key, new Set(values)]),
        ),
      }));
    } catch (error) {
      this.dropCacheEntry(this.cacheKey);
      return null;
    }
  }

  /** Store the catalogue for the next page load. Best-effort by design. */
  writeCachedProducts(products) {
    if (!this.config.cacheTtl || this.isDesignMode) return;
    // An empty read is more often a misconfigured collection than a genuinely
    // empty one; caching it would keep the result screen empty until the TTL.
    if (products.length === 0) return;

    const entry = {
      savedAt: Date.now(),
      // Named keys rather than a tuple: the entry carries the GA4 item data
      // as well as the matcher's, and a positional read of eleven fields is
      // one reorder away from silently mislabelling every product.
      products: products.map((product) => ({
        id: product.id,
        handle: product.handle,
        title: product.title,
        vendor: product.vendor,
        productType: product.productType,
        variantId: product.variantId,
        variantTitle: product.variantTitle,
        price: product.price,
        currency: product.currency,
        tagIds: Array.from(product.tagIds),
        fields: Array.from(product.fields, ([key, values]) => [
          key,
          Array.from(values),
        ]),
      })),
    };

    try {
      this.pruneCache();
      window.localStorage.setItem(this.cacheKey, JSON.stringify(entry));
    } catch (error) {
      // Out of quota, or storage is unavailable. The fetch already succeeded,
      // so fall back to the in-memory promise for this page view only.
      this.dropCacheEntry(this.cacheKey);
    }
  }

  /**
   * Drop our own expired entries before writing. Without this a shop with many
   * configurator collections slowly fills its localStorage quota with reads
   * nobody will use again. Entries written by an older cache version go too —
   * nothing will ever read them again.
   */
  pruneCache() {
    const maxAge = this.config.cacheTtl * 60000;
    const currentKey = this.cacheKey;
    const stale = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (!key || !key.startsWith(CACHE_ROOT) || key === currentKey) continue;
      if (key.startsWith(CACHE_PREFIX)) {
        try {
          const age =
            Date.now() - JSON.parse(window.localStorage.getItem(key)).savedAt;
          if (age >= 0 && age < maxAge) continue;
        } catch (error) {
          // Not valid JSON — drop it too.
        }
      }
      stale.push(key);
    }
    stale.forEach((key) => this.dropCacheEntry(key));
  }

  dropCacheEntry(key) {
    try {
      window.localStorage.removeItem(key);
    } catch (error) {
      // Nothing sensible to do — the catalogue is already in memory.
    }
  }

  async fetchAllProducts() {
    const {
      shopDomain,
      collectionHandle,
      token,
      apiVersion,
      language,
      country,
    } = this.config;
    if (!shopDomain || !token) {
      throw new Error("Missing Storefront API configuration");
    }

    const endpoint = `https://${shopDomain}/api/${apiVersion}/graphql.json`;
    // Without @inContext the API answers in the browser's Accept-Language,
    // which is not necessarily this storefront's language, and hands back
    // handles from that language — which the storefront then 301s away from,
    // so the card requests come back empty. Always say which language.
    const localeVars = `$language: LanguageCode, $country: CountryCode`;
    const inContext = `@inContext(language: $language, country: $country)`;
    const locale = { language, country };

    // The product metafields the tags' `connected` mappings point at. Declared
    // only when a quiz actually uses them, so a purely tag-driven quiz keeps
    // sending exactly the query it sent before. On a translated storefront they
    // are read separately, in the primary language — see attachPrimaryMetafields.
    const identifiers = this.metafieldIdentifiers;
    const inlineMetafields =
      identifiers.length > 0 && !this.needsPrimaryMetafieldRead;
    const metafieldVar = inlineMetafields
      ? `, $identifiers: [HasMetafieldsIdentifier!]!`
      : "";
    const metafieldField = inlineMetafields
      ? `metafields(identifiers: $identifiers) { namespace key value }`
      : "";
    if (inlineMetafields) locale.identifiers = identifiers;

    const productFields = `
      pageInfo { hasNextPage endCursor }
      nodes {
        id
        handle
        # Everything below only feeds the GA4 item payload of the result grid
        # (view_item_list / select_item); the matcher uses none of it.
        title
        vendor
        productType
        variants(first: 1) {
          nodes {
            id
            title
            price { amount currencyCode }
          }
        }
        configuratorTags: metafield(namespace: "custom", key: "configurator_tags") {
          references(first: 50) {
            nodes { ... on Metaobject { id } }
          }
        }
        ${metafieldField}
      }`;
    const collectionQuery = `
      query ConfiguratorCollectionProducts($handle: String!, $cursor: String, ${localeVars}${metafieldVar}) ${inContext} {
        collection(handle: $handle) {
          products(first: 250, after: $cursor, sortKey: COLLECTION_DEFAULT) { ${productFields} }
        }
      }`;
    const allProductsQuery = `
      query ConfiguratorAllProducts($cursor: String, ${localeVars}${metafieldVar}) ${inContext} {
        products(first: 250, after: $cursor, sortKey: BEST_SELLING) { ${productFields} }
      }`;

    const request = async (query, variables) => {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": token,
        },
        body: JSON.stringify({ query, variables }),
      });
      if (!response.ok)
        throw new Error(`Storefront API responded ${response.status}`);
      const payload = await response.json();
      if (payload.errors && payload.errors.length) {
        throw new Error(payload.errors.map((e) => e.message).join("; "));
      }
      return payload.data || {};
    };

    const products = [];
    let cursor = null;
    // Start from the collection; fall back to all products when the handle
    // does not resolve to a stored collection (e.g. the virtual /collections/all
    // route has no Collection object, so collection(handle) is null).
    let mode = collectionHandle ? "collection" : "all";

    // Cap iterations so a misconfiguration can never spin forever (fallback + 20 pages).
    for (let guard = 0; guard < 40; guard++) {
      let connection;
      if (mode === "collection") {
        const data = await request(collectionQuery, {
          ...locale,
          handle: collectionHandle,
          cursor,
        });
        if (!data.collection) {
          mode = "all";
          cursor = null;
          continue;
        }
        connection = data.collection.products;
      } else {
        const data = await request(allProductsQuery, { ...locale, cursor });
        connection = data.products;
      }
      if (!connection) break;

      connection.nodes.forEach((node) => {
        const refs =
          (node.configuratorTags && node.configuratorTags.references.nodes) ||
          [];
        // The first variant stands in for the product in the GA4 item: the
        // grid links to the product page, not to a variant.
        const variant = (node.variants && node.variants.nodes[0]) || null;
        products.push({
          id: node.id,
          handle: node.handle,
          title: node.title,
          vendor: node.vendor,
          productType: node.productType,
          variantId: variant ? variant.id : "",
          variantTitle: variant ? variant.title : "",
          price: variant ? variant.price.amount : "",
          currency: variant ? variant.price.currencyCode : "",
          tagIds: new Set(refs.map((ref) => normalizeTagId(ref.id))),
          fields: toFieldMap(node.metafields),
        });
      });

      if (!connection.pageInfo.hasNextPage) break;
      cursor = connection.pageInfo.endCursor;
    }

    if (this.needsPrimaryMetafieldRead) {
      await this.attachPrimaryMetafields(products, request);
    }

    return products;
  }

  /**
   * Text metafields are translatable — custom.application answers
   * "Aufputzmontage" on the German storefront and "Surface mounting" on the
   * English one. A tag's `connected` value is written once, so matching has to
   * compare against one fixed language: the shop's primary one. On a translated
   * storefront the localised read still supplies the handles (those are
   * translated too, and a foreign handle only redirects), and the match values
   * come from this second read instead.
   */
  get needsPrimaryMetafieldRead() {
    const { language, primaryLanguage } = this.config;
    return (
      this.metafieldIdentifiers.length > 0 &&
      Boolean(language) &&
      Boolean(primaryLanguage) &&
      language !== primaryLanguage
    );
  }

  /**
   * Read the connected metafields with @inContext pinned to the primary
   * language, and fold them onto the products. Looked up by id, the one
   * identifier that is the same in every language.
   *
   * The language has to be spelled out: a query without @inContext does not
   * fall back to the shop's primary language but to the browser's
   * Accept-Language header, so an English browser on a translated storefront
   * would get the translated values back a second time and match nothing.
   */
  async attachPrimaryMetafields(products, request) {
    const query = `
      query ConfiguratorPrimaryMetafields($ids: [ID!]!, $identifiers: [HasMetafieldsIdentifier!]!, $language: LanguageCode) @inContext(language: $language) {
        nodes(ids: $ids) {
          ... on Product {
            id
            metafields(identifiers: $identifiers) { namespace key value }
          }
        }
      }`;

    const byId = new Map(products.map((product) => [product.id, product]));
    const ids = Array.from(byId.keys());

    for (let start = 0; start < ids.length; start += PRIMARY_READ_BATCH) {
      const data = await request(query, {
        ids: ids.slice(start, start + PRIMARY_READ_BATCH),
        identifiers: this.metafieldIdentifiers,
        language: this.config.primaryLanguage,
      });
      (data.nodes || []).forEach((node) => {
        const product = node && byId.get(node.id);
        if (product) product.fields = toFieldMap(node.metafields);
      });
    }
  }

  restart() {
    // Reported before the reset, while the screen it was clicked from and the
    // result count it walked away from are still readable.
    if (this.tracking) {
      this.tracking.restart(
        this.result.hidden ? this.tracking.position(this.current) : "results",
      );
    }
    // Retire any in-flight render so its cards cannot land after the reset.
    this.run = (this.run || 0) + 1;
    // Start over on the pre-answers too: the required groups are seeded once,
    // and a restart is exactly the moment that "once" comes round again.
    this.seeded.clear();
    this.interactionPriority = 0;
    this.querySelectorAll(".ccq__group").forEach((group) => {
      const groupId = group.dataset.groupId;
      this.clearGroup(groupId);
      this.selectionPriorities.set(groupId, 0);
      this.syncGroup(group);
    });
    this.hideAdjustmentStatus();
    this.gridEl.innerHTML = "";
    this.countEl.hidden = true;
    this.moreBtn.hidden = true;
    this.setLoading(false);
    this.setStatus("");
    this.result.hidden = true;
    this.card.hidden = false;
    if (this.intro) this.intro.hidden = false;
    this.go(0);
  }

  scrollIntoViewIfNeeded() {
    if (this.getBoundingClientRect().top < 0) {
      this.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  /**
   * On a phone a question can be taller than the screen, so the buttons sit a
   * long way below the bar. Every step change lands on the progress bar — not
   * the top of the section, which is the intro — whichever way the page has to
   * move, and a bar already in place stays put.
   */
  alignProgress() {
    if (!this.progressEl) return;
    const delta = this.progressEl.getBoundingClientRect().top;
    // Floored: rounding up would clip the bar's top edge by a fraction of a px.
    const target = Math.max(
      0,
      Math.floor(window.scrollY + delta - this.stickyHeaderOffset(delta < 0)),
    );
    if (Math.abs(target - window.scrollY) < 1) return;
    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    window.scrollTo({ top: target, behavior: reduced ? "instant" : "smooth" });
  }

  /**
   * How much of the viewport's top the header will cover once the scroll has
   * settled. The header is `.strix-main-header`, which does not publish
   * --header-height: it is pinned in "sticky" mode, shows only while scrolling
   * up in "on_scroll" mode, and scrolls away in "none" — what "spluss" runs on
   * phones. Pinned, it is past 40px of scroll and so in its compact form.
   */
  stickyHeaderOffset(scrollingUp) {
    const header = document.querySelector(".strix-main-header");
    if (!header) return 0;
    const pinned =
      header.classList.contains("is--always-sticky") ||
      (scrollingUp && header.classList.contains("is--on-scroll-mode"));
    if (!pinned) return 0;
    const compact = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue(
        "--strix-main-header-compact",
      ),
    );
    return Number.isFinite(compact) ? compact : header.offsetHeight;
  }
}

if (!customElements.get("configurator-quiz")) {
  customElements.define("configurator-quiz", ConfiguratorQuiz);
}
